import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  BarChart,
  Bar,
  TooltipProps,
} from 'recharts';
import { InfoCard } from '@backstage/core-components';
import {
  makeStyles,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip as MuiTooltip,
  Button as MuiButton,
  useTheme,
  Paper,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { transformGrafanaResponse } from '../../utils/dataTransformer';
import { generateRandomColor } from '../../utils/chartUtils';
import { QueryResponse } from '../../types/grafana';
import { logLevelColors } from '../../utils';

const useStyles = makeStyles(theme => ({
  chartContainer: {
    height: props => ((props as any).fullscreen ? 'calc(100vh - 200px)' : 400),
    transition: 'height 0.3s ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
  },
  formControl: {
    margin: theme.spacing(0, 1),
    minWidth: 120,
  },
  noDataMessage: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  lastRefreshed: {
    marginRight: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  refreshButton: {
    marginTop: theme.spacing(2),
  },
  customTooltip: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },
  tooltipLabel: {
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  tooltipEntry: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  tooltipColor: {
    width: 10,
    height: 10,
    marginRight: theme.spacing(1),
    borderRadius: '50%',
  },
  tooltipName: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  tooltipValue: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
}));

type TimeSeriesChartProps = {
  data: QueryResponse;
  title: string;
  onRefresh?: () => void;
  lastRefreshed?: Date | null;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  colors,
  classes,
}: TooltipProps<number, string> & { colors: string[]; classes: any }) => {
  if (active && payload && payload.length) {
    return (
      <Paper className={classes.customTooltip}>
        <div className={classes.tooltipLabel}>{label}</div>
        {payload.map((entry: any, index: number) => (
          <div key={`tooltip-${index}`} className={classes.tooltipEntry}>
            <div
              className={classes.tooltipColor}
              style={{
                backgroundColor: entry.color || colors[index % colors.length],
              }}
            />
            <span className={classes.tooltipName}>{entry.name}:</span>
            <span className={classes.tooltipValue}>{entry.value}</span>
          </div>
        ))}
      </Paper>
    );
  }

  return null;
};

export const TimeSeriesChart = ({
  data,
  title,
  onRefresh,
  lastRefreshed,
}: TimeSeriesChartProps) => {
  const [fullscreen, setFullscreen] = useState(false);
  const theme = useTheme();
  const classes = useStyles({ fullscreen });
  const [chartType, setChartType] = useState<'line' | 'area' | 'bar'>('line');
  const [chartData, setChartData] = useState<any[]>([]);
  const [series, setSeries] = useState<string[]>([]);

  useEffect(() => {
    const transformedData = transformGrafanaResponse(data);
    setChartData(transformedData);

    const allKeys = new Set<string>();
    transformedData.forEach(point => {
      Object.keys(point).forEach(key => {
        if (key !== 'timestamp') {
          allKeys.add(key);
        }
      });
    });

    setSeries(Array.from(allKeys));
  }, [data]);

  if (!chartData || chartData.length === 0) {
    return (
      <InfoCard title={title}>
        <div className={classes.noDataMessage}>
          <Typography variant="body1" gutterBottom>
            Nenhum dado disponível para exibição
          </Typography>
          {onRefresh && (
            <MuiButton
              onClick={onRefresh}
              color="primary"
              variant="outlined"
              className={classes.refreshButton}
            >
              Atualizar
            </MuiButton>
          )}
        </div>
      </InfoCard>
    );
  }

  const isLogData = series.some(s =>
    ['error', 'warn', 'info', 'debug'].includes(s.toLowerCase()),
  );

  const colors = series.map(s => {
    const lowerS = s.toLowerCase();
    return (logLevelColors as any)[lowerS] || generateRandomColor();
  });

  const formatLastRefreshed = () => {
    if (!lastRefreshed) return '';
    return `Atualizado em: ${lastRefreshed.toLocaleTimeString()}`;
  };

  const brushConfig = {
    dataKey: 'timestamp',
    height: 30,
    stroke: '#8884d8',
    fill: theme.palette.background.paper,
    fillOpacity: 0.3,
    travellerWidth: 10,
  };

  const legendProps = {
    wrapperStyle: {
      paddingTop: 20, // Adiciona espaço acima da legenda
      marginTop: 10, // Adiciona margem acima da legenda
    },
    verticalAlign: 'bottom' as const,
    height: 36,
  };

  const renderChart = () => {
    if (isLogData && chartType === 'bar') {
      return (
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="timestamp"
            label={{
              value: 'Tempo',
              position: 'insideBottomRight',
              offset: -10,
            }}
          />
          <YAxis />
          <Tooltip
            content={<CustomTooltip colors={colors} classes={classes} />}
          />
          <Legend {...legendProps} />
          <Brush {...brushConfig} />

          {series.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colors[index]}
              name={key}
              stackId="stack"
            />
          ))}
        </BarChart>
      );
    }

    return (
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="timestamp"
          label={{ value: 'Tempo', position: 'insideBottomRight', offset: -10 }}
        />
        <YAxis />
        <Tooltip
          content={<CustomTooltip colors={colors} classes={classes} />}
        />
        <Legend {...legendProps} />
        <Brush {...brushConfig} />

        {series.map((key, index) => (
          <Line
            key={key}
            type={chartType === 'area' ? 'monotone' : 'linear'}
            dataKey={key}
            stroke={colors[index]}
            fill={chartType === 'area' ? `${colors[index]}80` : undefined}
            activeDot={{ r: 8 }}
            name={key}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    );
  };

  return (
    <InfoCard title={title}>
      <div className={classes.header}>
        <Typography variant="caption" className={classes.lastRefreshed}>
          {formatLastRefreshed()}
        </Typography>

        <div className={classes.controls}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.formControl}
          >
            <InputLabel id="chart-type-label">Tipo</InputLabel>
            <Select
              labelId="chart-type-label"
              id="chart-type"
              value={chartType}
              onChange={e =>
                setChartType(e.target.value as 'line' | 'area' | 'bar')
              }
              label="Tipo"
            >
              <MenuItem value="line">Linha</MenuItem>
              <MenuItem value="area">Área</MenuItem>
              {isLogData && <MenuItem value="bar">Barra</MenuItem>}
            </Select>
          </FormControl>

          {onRefresh && (
            <MuiTooltip title="Atualizar dados">
              <IconButton onClick={onRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            </MuiTooltip>
          )}

          <MuiTooltip title={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}>
            <IconButton onClick={() => setFullscreen(!fullscreen)} size="small">
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </MuiTooltip>
        </div>
      </div>

      <div className={classes.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </InfoCard>
  );
};
